// frontend/src/pages/Login.tsx
import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface LoginForm {
  email: string;
  password: string;
}

export default function Login() {
  const { login, user } = useAuth();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<LoginForm>();
  const [showPassword, setShowPassword] = useState(false);

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (data: LoginForm) => {
    try {
      await login(data.email, data.password);
      toast.success('Connexion réussie !');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row relative">
      {/* Background Container with uniform blue-gray color */}
      <div
        className="absolute inset-0"
        style={{background: "linear-gradient(to right, rgba(55, 65, 81, 0.2), rgba(55, 65, 81, 0.2))"}}
      ></div>

      {/* Partie gauche - Formulaire de connexion */}
      <div className="relative z-10 w-full lg:w-1/2 min-h-screen flex items-center justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-sm sm:max-w-md w-full relative">
          {/* Arrière-plan décoratif en forme de carte décalée (plus petit sur mobile) */}
          <div
            className="absolute inset-0 transform rotate-1 translate-x-1 translate-y-1 sm:rotate-2 sm:translate-x-2 sm:translate-y-2 rounded-lg sm:rounded-xl shadow-lg"
            style={{backgroundColor: '#3b82f6', opacity: 0.8, zIndex: -1}}
          ></div>
          <div
            className="absolute inset-0 transform -rotate-1 -translate-x-1 -translate-y-1 bg-blue-400 rounded-lg sm:rounded-xl shadow-lg"
            style={{opacity: 0.6, zIndex: -2}}
          ></div>

          {/* Conteneur principal avec cadre professionnel (padding réduit sur mobile) */}
          <div className="bg-white/95 backdrop-blur-sm rounded-lg sm:rounded-xl shadow-xl border border-white/20 p-5 sm:p-8 space-y-5 sm:space-y-8 relative z-10">
            <div>
              <div className="flex justify-center mb-3 mt-1 sm:mt-2">
                <img
                  src="/logo/TELEXCEL_Logo_Login.png"
                  alt="TELEXCEL"
                  className="h-2 w-auto object-contain filter drop-shadow-sm"
                  style={{
                    imageRendering: 'crisp-edges',
                    maxWidth: '190px',
                    height: '100px',
                    marginTop: '0px',
                    marginBottom: '0px'
                  }}
                />
              </div>
              <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900">
                Connexion à votre compte
              </h2>
              <p className="mt-1 sm:mt-2 text-center text-sm text-gray-600">
                Entrez vos identifiants pour accéder à la plateforme.
              </p>
            </div>
            
            <form className="mt-6 sm:mt-8 space-y-5 sm:space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-4 sm:space-y-5">
                {/* Champ Email avec label visible */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Email
                  </label>
                  <input
                    {...register('email', { 
                      required: 'Email requis',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Email invalide'
                      }
                    })}
                    type="email"
                    autoComplete="email"
                    className="appearance-none block w-full px-4 py-3 sm:py-3.5 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-all"
                    placeholder="Entrez votre email"
                    inputMode="email"
                  />
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>
                
                {/* Champ Mot de passe avec label visible et icône œil */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Mot de passe
                  </label>
                  <div className="relative">
                    <input
                      {...register('password', { 
                        required: 'Mot de passe requis',
                        minLength: {
                          value: 6,
                          message: 'Minimum 6 caractères'
                        }
                      })}
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      className="appearance-none block w-full px-4 py-3 sm:py-3.5 pr-12 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-all"
                      placeholder="Entrez votre mot de passe"
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-2 text-sm text-red-600">{errors.password.message}</p>
                  )}
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group relative w-full flex justify-center py-3 sm:py-3.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 shadow-lg transition-all transform hover:scale-105 active:scale-[0.99]"
                >
                  {isSubmitting ? 'Connexion...' : 'Se connecter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Partie droite - Zone plus sombre avec overlay décoratif */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        {/* Image de fond avec overlay sombre */}
        <img
          src="/background_login.jpg"
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlay sombre pour maintenir la progression du gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-gray-700/20 to-gray-900/60"></div>
      </div>
    </div>
  );
}
